import { useState, useRef, useEffect, useCallback } from "react";
import { Send, Bot, User, FileText } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeSanitize from "rehype-sanitize";
import type { Message } from "../types/chat.types";
import {
  sendMessage,
  saveChatSession,
  loadChatSession,
} from "../services/chatService";
import { useUser } from "@foundation/altzero_base/base/contextapi/UserContext";
import { supabase } from "@foundation/altzero_base/base/utils/supabaseClient";
import { theme } from "../../../theme";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

const FOLLOW_UP_QUESTION_POOL = [
  "What are the red flags that require calling 000 immediately?",
  "How do I perform a sideline concussion assessment?",
  "What is the graduated return-to-play protocol?",
  "What symptoms indicate a concussion?",
  "When should a player be removed from play?",
  "What does 'if in doubt, sit them out' mean?",
  "How long does concussion recovery typically take?",
  "Can a player return the same day after a concussion?",
  "What is CRT6 and how do I use it on the sideline?",
  "What should I do if a player loses consciousness?",
  "How do I explain concussion to a parent or guardian?",
  "What is post-concussion syndrome?",
  "How do I support an athlete returning to sport after concussion?",
  "What are the AIS guidelines for junior athletes?",
  "How do I log a concussion incident in the app?",
  "What is the difference between a concussion and a head knock?",
  "Are there specific rules for children with concussion?",
  "Can someone have a concussion without hitting their head?",
  "What questions should I ask a player to screen for concussion?",
  "What should coaches know about concussion in contact sports?",
];

const MAX_INPUT_LENGTH = 500;

export const ChatPage = () => {
  const { user } = useUser();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [orgId, setOrgId] = useState<string | undefined>(undefined);
  const [showSources, setShowSources] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sessionLoadedRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const usedQuestionIndicesRef = useRef(new Set<number>());

  // Pick 3 random questions from the pool, never repeating within a session
  const pickFollowUpQuestions = useCallback((): string[] => {
    const pool = FOLLOW_UP_QUESTION_POOL;
    const used = usedQuestionIndicesRef.current;
    if (pool.length - used.size < 3) used.clear();
    const available = pool.reduce<number[]>((acc, _, i) => {
      if (!used.has(i)) acc.push(i);
      return acc;
    }, []);
    for (let i = available.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [available[i], available[j]] = [available[j], available[i]];
    }
    const picked = available.slice(0, 3);
    picked.forEach((i) => used.add(i));
    return picked.map((i) => pool[i]);
  }, []);

  // 3 random questions shown on empty state (computed once on mount)
  const [initialSuggestions] = useState<string[]>(() => {
    const available = FOLLOW_UP_QUESTION_POOL.map((_, i) => i);
    for (let i = available.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [available[i], available[j]] = [available[j], available[i]];
    }
    return available.slice(0, 3).map((i) => FOLLOW_UP_QUESTION_POOL[i]);
  });

  // Load session on mount
  useEffect(() => {
    const session = loadChatSession();
    if (session) {
      setMessages(session.messages);
      sessionLoadedRef.current = true;
    }
  }, []);

  // Resolve orgId from Supabase session or org membership
  useEffect(() => {
    if (!user?.id) return;

    const resolveOrgId = async () => {
      try {
        // Try user_metadata first (set during signup or org switch)
        const metaOrgId = user.user_metadata?.org_id;
        if (metaOrgId) {
          setOrgId(metaOrgId as string);
          return;
        }

        // Fallback: query org membership directly
        const { data, error } = await supabase
          .from('base_organisation_members')
          .select('organisation_id')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .limit(1)
          .single();

        if (!error && data?.organisation_id) {
          setOrgId(data.organisation_id as string);
          return;
        }

        // Last resort: check base_profiles for tenant_id
        const { data: profile } = await supabase
          .from('base_profiles')
          .select('tenant_id')
          .eq('id', user.id)
          .not('tenant_id', 'is', null)
          .limit(1)
          .single();

        if (profile?.tenant_id) {
          setOrgId(profile.tenant_id as string);
        } else {
          console.warn('Could not resolve org ID for user:', user.id);
        }
      } catch (err) {
        console.error('Failed to resolve org ID for chat:', err);
      }
    };

    resolveOrgId();
  }, [user?.id]);

  // Debounced session save — avoids thrashing during streaming
  useEffect(() => {
    if (messages.length === 0) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveChatSession(messages, "knowledge-base");
    }, 500);
    return () => clearTimeout(saveTimerRef.current);
  }, [messages, provider]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  // Auto-scroll when mobile keyboard opens
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const handleResize = () => scrollToBottom();
    viewport.addEventListener("resize", handleResize);
    return () => viewport.removeEventListener("resize", handleResize);
  }, [scrollToBottom]);

  const handleSetRaw = (messageId: string, showRaw: boolean) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, showRaw } : m))
    );
  };

  const handleSend = async (content?: string) => {
    const messageContent = content || input.trim();
    if (!messageContent || isTyping) return;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: "user",
      content: messageContent,
      timestamp: new Date(),
    };

    const currentMessages = [...messages, userMessage];
    setMessages(currentMessages);
    setInput("");
    setIsTyping(true);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }

    // Both providers use non-streaming POST /api/charlie-chat
    // (Certant API does not support the /query/stream endpoint)
    try {
      const response = await sendMessage(messageContent, messages, "knowledge-base", user?.id, orgId);
      setMessages((prev) => [...prev, { ...response, followUpQuestions: pickFollowUpQuestions() }]);
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_INPUT_LENGTH) {
      setInput(value);
    }
    const textarea = e.target;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  const knowledgeReady = !!user?.id;

  // Find the last assistant message for follow-up display
  const lastAssistantMessage = [...messages].reverse().find(
    (m) => m.role === "assistant" && !m.streaming && !m.error
  );

  return (
    <div className={theme.chat.page}>
      {/* Chat card */}
      <div className={theme.chat.card}>

        {/* Sources toggle toolbar */}
        <div className="flex items-center gap-2 px-4 py-2 border-b dark:border-white/[0.06] border-black/[0.06]">
          <button
            onClick={() => setShowSources((prev) => !prev)}
            aria-label={showSources ? "Hide sources" : "Show sources"}
            aria-pressed={showSources}
            className={`${theme.providerToggle.base} ml-auto ${
              showSources
                ? "dark:bg-teal-500/[0.12] dark:border-teal-500/[0.25] dark:text-teal-400 bg-teal-50 border border-teal-200 text-teal-700"
                : theme.providerToggle.inactive
            }`}
          >
            <FileText className="w-3 h-3" />
            Sources
          </button>
        </div>

        {/* Messages area */}
        <div
          className={theme.chat.messagesArea}
          role="log"
          aria-label="Chat conversation"
          aria-live="polite"
        >
          <div className="mx-auto space-y-4">
            {messages.length === 0 && (
              <div className={`flex flex-col items-center justify-center h-full min-h-[300px] px-4 py-8 ${theme.anim.fadeInUp}`}>
                {/* Bot avatar */}
                <div className="relative mb-5">
                  <div className="w-16 h-16 rounded-2xl dark:bg-sy-teal/[0.12] bg-teal-50 dark:border-sy-teal/[0.20] border border-teal-100 flex items-center justify-center">
                    <Bot className="w-8 h-8 dark:text-sy-teal text-teal-600" />
                  </div>
                  {/* Online indicator */}
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-sy-green border-2 dark:border-sy-navy border-white flex items-center justify-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  </div>
                </div>

                <h2 className={`text-xl font-bold ${theme.text.heading} mb-2 text-center`}>
                  Ask Charlie
                </h2>
                <p className={`${theme.text.body} text-sm text-center max-w-xs leading-relaxed mb-1`}>
                  Concussion management, sideline assessment, and return-to-play guidance — grounded in CRT6 and AIS guidelines.
                </p>

                <div className="mb-6" />

                {/* Suggested questions */}
                <div className="flex flex-wrap justify-center gap-2 max-w-sm">
                  {initialSuggestions.map((question) => (
                    <button
                      key={question}
                      onClick={() => handleSend(question)}
                      aria-label={`Ask: ${question}`}
                      className={`${theme.button.pillBase} ${theme.button.pillTeal}`}
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((message) => (
              <div
                key={message.id}
                aria-label={`Message from ${message.role === "user" ? "you" : "Charlie"}`}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`flex items-start space-x-3 max-w-[80%] ${
                    message.role === "user" ? "flex-row-reverse space-x-reverse" : ""
                  }`}
                >
                  <div className={message.role === "user" ? theme.chat.userAvatar : theme.chat.botAvatar}>
                    {message.role === "user" ? (
                      <User className="w-4 h-4" />
                    ) : (
                      <Bot className={`w-4 h-4 ${theme.text.accent}`} />
                    )}
                  </div>
                  <div>
                    <div className={message.role === "user" ? theme.chat.userBubble : theme.chat.aiBubble}>
                      {message.role === "user" ? (
                        <p className="text-base leading-relaxed whitespace-pre-wrap">{message.content}</p>
                      ) : message.streaming && !message.content ? (
                        <span className="flex items-center gap-1.5 py-1" role="status">
                          <span className={`w-1.5 h-1.5 dark:bg-sy-teal bg-teal-600 rounded-full ${theme.anim.bounce}`} style={{ animationDelay: "0ms" }} />
                          <span className={`w-1.5 h-1.5 dark:bg-sy-teal bg-teal-600 rounded-full ${theme.anim.bounce}`} style={{ animationDelay: "160ms" }} />
                          <span className={`w-1.5 h-1.5 dark:bg-sy-teal bg-teal-600 rounded-full ${theme.anim.bounce}`} style={{ animationDelay: "320ms" }} />
                          <span className="sr-only">Charlie is thinking</span>
                        </span>
                      ) : (
                        <>
                          {message.showRaw && message.rawContent ? (
                            <div className="chat-markdown">
                              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} rehypePlugins={[rehypeSanitize]}>
                                {message.rawContent}
                              </ReactMarkdown>
                            </div>
                          ) : (
                            <div className="chat-markdown">
                              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} rehypePlugins={[rehypeSanitize]}>
                                {message.content}
                              </ReactMarkdown>
                            </div>
                          )}
                          {/* Raw / Skill-Enhanced toggle — shown on every completed assistant message */}
                          {!message.streaming && message.role === "assistant" && (
                            <div className={`flex items-center gap-2 mt-2 pt-2 border-t ${theme.border.subtle}`}>
                              <span className={`text-xs ${theme.text.veryMuted}`}>View:</span>
                              {message.skillApplied ? (
                                <>
                                  <button
                                    onClick={() => handleSetRaw(message.id, false)}
                                    className={`text-xs px-2 py-0.5 rounded transition-colors
                                      ${!message.showRaw
                                        ? "dark:bg-teal-500/20 bg-teal-50 dark:text-teal-300 text-teal-700 font-medium"
                                        : `${theme.text.muted} hover:dark:text-white/70`}`}
                                  >
                                    Skill-Enhanced
                                  </button>
                                  <button
                                    onClick={() => handleSetRaw(message.id, true)}
                                    className={`text-xs px-2 py-0.5 rounded transition-colors
                                      ${message.showRaw
                                        ? "dark:bg-amber-500/20 bg-amber-50 dark:text-amber-300 text-amber-700 font-medium"
                                        : `${theme.text.muted} hover:dark:text-white/70`}`}
                                  >
                                    Raw
                                  </button>
                                </>
                              ) : (
                                <span className={`text-xs ${theme.text.veryMuted} opacity-50`} title="No skill active">
                                  Raw | Skill-Enhanced (no skill active)
                                </span>
                              )}
                            </div>
                          )}
                        </>
                      )}
                      {showSources && message.sources && message.sources.length > 0 && !message.streaming && (
                        <div className={`mt-2 pt-2 border-t ${theme.border.subtle}`}>
                          <p className={`text-xs ${theme.text.muted}`}>
                            Sources: {message.sources.join(", ")}
                          </p>
                        </div>
                      )}
                      {message.provider && message.role === "assistant" && !message.streaming && (
                        <p className={`text-xs mt-1 ${theme.text.veryMuted}`}>
                          via Knowledge Base
                        </p>
                      )}
                    </div>

                    {/* Follow-up questions after the last assistant message */}
                    {message.role === "assistant" &&
                      !message.streaming &&
                      message.id === lastAssistantMessage?.id &&
                      message.followUpQuestions &&
                      message.followUpQuestions.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {message.followUpQuestions.map((question) => (
                            <button
                              key={question}
                              onClick={() => handleSend(question)}
                              disabled={isTyping}
                              aria-label={`Ask: ${question}`}
                              className={`${theme.button.pillBase} ${theme.button.pillTeal} disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {question}
                            </button>
                          ))}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && !messages.some((m) => m.streaming) && (
              <div className="flex justify-start" role="status">
                <div className="flex items-center space-x-3">
                  <div className={theme.chat.botAvatar}>
                    <Bot className={`w-4 h-4 ${theme.text.accent}`} />
                  </div>
                  <div className={theme.chat.aiBubble}>
                    <span className="flex items-center gap-1.5 py-1">
                      <span className={`w-1.5 h-1.5 dark:bg-sy-teal bg-teal-600 rounded-full ${theme.anim.bounce}`} style={{ animationDelay: "0ms" }} />
                      <span className={`w-1.5 h-1.5 dark:bg-sy-teal bg-teal-600 rounded-full ${theme.anim.bounce}`} style={{ animationDelay: "160ms" }} />
                      <span className={`w-1.5 h-1.5 dark:bg-sy-teal bg-teal-600 rounded-full ${theme.anim.bounce}`} style={{ animationDelay: "320ms" }} />
                    </span>
                    <span className="sr-only">Charlie is thinking</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input bar */}
        <div className={theme.chat.inputBar}>
          <div className="flex items-center justify-center space-x-3">
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                placeholder="Ask Charlie about concussion management..."
                maxLength={MAX_INPUT_LENGTH}
                rows={1}
                aria-label="Type your message to Charlie"
                aria-describedby="chat-disclaimer"
                className={`${theme.input.textarea} pr-12`}
              />
              <span
                className={`absolute right-3 bottom-2 text-xs ${
                  input.length > MAX_INPUT_LENGTH * 0.9 ? "text-red-400" : theme.text.veryMuted
                }`}
              >
                {input.length}/{MAX_INPUT_LENGTH}
              </span>
            </div>
            <button
              onClick={() => handleSend()}
              disabled={!input.trim() || isTyping || !knowledgeReady}
              aria-label="Send message"
              className={theme.button.iconSend}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          {!knowledgeReady && (
            <p className={`text-xs text-center py-1 text-amber-400`}>
              Sign in to use Knowledge Base chat
            </p>
          )}
          <p id="chat-disclaimer" className={theme.chat.disclaimer}>
            Charlie is not a medical device. Always consult a medical professional
            for suspected concussions.
          </p>

        </div>

      </div>
    </div>
  );
};
